import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import QuickAddTaskModal from '../features/quickadd/QuickAddTaskModal';
import Modal from './Modal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Förenklad mobilfooter för sidor utanför Workspace (Estimat, Kalkyl, Offert).
 * Visar: Ny (snabbuppgift), Uppdatera, Feedback
 */
const PageMobileFooter = ({ onRefresh }) => {
  const { user } = useAuth();

  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('Bugg');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: feedbackType,
          description: feedbackMessage,
          pageUrl: window.location.href,
        }),
      });
      if (res.ok) {
        setFeedbackSuccess(true);
        setFeedbackMessage('');
        setFeedbackType('Bugg');
        setTimeout(() => {
          setFeedbackSuccess(false);
          setShowFeedback(false);
        }, 2000);
      }
    } catch (err) {
      // Tyst felhantering
    }
    setFeedbackSending(false);
  };

  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh?.();
    } catch (e) {
      // Tyst
    }
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const canQuickAdd = ['byggkonsult', 'inredningsarkitekt', 'byggare'].includes(user?.role);

  return (
    <>
      {/* Spacer */}
      <div className="h-14 md:hidden" />

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.08)] md:hidden">
        <div className="flex items-center justify-around px-2 py-2">
          {/* Snabbuppgift */}
          {canQuickAdd && (
            <button
              onClick={() => setShowQuickAdd(true)}
              className="flex flex-col items-center gap-0.5 px-3 py-1 text-leadme-gray hover:text-leadme-pink transition-colors"
              title="Snabbuppgift"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-[10px]">Ny</span>
            </button>
          )}

          {/* Refresh */}
          <button
            onClick={handleRefreshClick}
            className="flex flex-col items-center gap-0.5 px-3 py-1 text-leadme-gray hover:text-leadme-pink transition-colors"
            title="Uppdatera"
          >
            <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-[10px]">Uppdatera</span>
          </button>

          {/* Feedback */}
          <button
            onClick={() => setShowFeedback(!showFeedback)}
            className="flex flex-col items-center gap-0.5 px-3 py-1 text-leadme-gray hover:text-leadme-pink transition-colors"
            title="Feedback"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            <span className="text-[10px]">Feedback</span>
          </button>
        </div>
      </div>

      {/* Feedback modal */}
      <Modal isOpen={showFeedback} onClose={() => setShowFeedback(false)} size="small">
        <div className="p-5">
          {feedbackSuccess ? (
            <div className="text-center py-8">
              <svg className="w-14 h-14 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-gray-700">Tack för din feedback!</p>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Skicka feedback</h2>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Typ</label>
                <div className="flex gap-2">
                  {['Bugg', 'Förslag', 'Övrigt'].map(typ => (
                    <button
                      key={typ}
                      onClick={() => setFeedbackType(typ)}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                        feedbackType === typ
                          ? 'bg-leadme-pink text-white border-leadme-pink'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-leadme-pink'
                      }`}
                    >
                      {typ}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Beskrivning</label>
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder="Beskriv problemet eller förslaget..."
                  className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowFeedback(false)}
                  className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleSendFeedback}
                  disabled={feedbackSending || !feedbackMessage.trim()}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    !feedbackMessage.trim() || feedbackSending
                      ? 'bg-gray-300 text-gray-400 cursor-not-allowed'
                      : 'bg-leadme-pink text-white hover:bg-leadme-pink-dark'
                  }`}
                >
                  {feedbackSending ? 'Skickar...' : 'Skicka'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* QuickAdd modal */}
      <QuickAddTaskModal isOpen={showQuickAdd} onClose={() => setShowQuickAdd(false)} />
    </>
  );
};

export default PageMobileFooter;
